import { useConversation } from '@11labs/react';
import { useState, useCallback } from 'react';

// TODO: Replace with your actual ElevenLabs Agent ID
const ELEVENLABS_AGENT_ID = 'sCiibZby05pVdkAhyCeU';

export function useElevenLabsConversation() {
  const [currentError, setCurrentError] = useState(null);
  const [messages, setMessages] = useState([]); // To store conversation messages

  const handleNewMessage = useCallback((message) => {
    console.log("Received message from ElevenLabs:", message);
    setMessages(prevMessages => [...prevMessages, message]);
    // If a component using this hook needs to react to messages,
    // it can access the `messages` array from the hook's return value.
  }, []);

  const handleError = useCallback((error) => {
    console.error("ElevenLabs conversation error:", error);
    setCurrentError(error.message || 'An unknown error occurred.');
    // If a component using this hook needs to react to errors,
    // it can access the `error` state from the hook's return value.
  }, []);

  const {
    startSession,
    endSession,
    sendUserMessage,
    sendContextualUpdate,
    sendFeedback,
    status,
    isSpeaking,
    canSendFeedback,
    // Note: onMessage and onError are passed in the options, not returned here
  } = useConversation({
    onConnect: () => {
      console.log("ElevenLabs conversation connected");
      setCurrentError(null); // Clear error on new connection
    },
    onDisconnect: () => {
      console.log("ElevenLabs conversation disconnected");
    },
    onMessage: handleNewMessage,
    onError: handleError,
    // You can add other options here if needed, for example:
    // overrides: {
    //   agent: {
    //     firstMessage: "Hello! How can I help you today?",
    //     language: "en",
    //   },
    //   tts: {
    //     voiceId: "your_custom_voice_id", // if you have a custom voice
    //   },
    // },
  });

  const handleStartConversation = useCallback(async (priorCustomerContext, personId, latestMemory) => {
    setCurrentError(null);
    setMessages([]); // Clear messages from previous session
    if (!ELEVENLABS_AGENT_ID || ELEVENLABS_AGENT_ID === 'ELEVENLABS_AGENT_ID') {
      const errorMsg = "ElevenLabs Agent ID is not configured. Please set it in ConversationService.js";
      console.error(errorMsg);
      setCurrentError(errorMsg);
      alert(errorMsg); // Simple alert for now
      return null;
    }

    try {
      // Request microphone permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("Microphone permission granted.");

      // For public agents, agentId is sufficient
      const sessionOptions = { 
        agentId: ELEVENLABS_AGENT_ID,
        dynamicVariables: {}
      };

      if (priorCustomerContext && priorCustomerContext.trim() !== '') {
        sessionOptions.dynamicVariables.rise_user_context = priorCustomerContext;
      }

      if (personId && personId.trim() !== '') {
        sessionOptions.dynamicVariables.person_id = personId;
      }

      if (latestMemory && latestMemory.trim() !== '') {
        sessionOptions.dynamicVariables.rise_memory = latestMemory;
      }

      // Remove dynamicVariables if it's empty
      if (Object.keys(sessionOptions.dynamicVariables).length === 0) {
        delete sessionOptions.dynamicVariables;
      }

      const sessionId = await startSession(sessionOptions);
      console.log("ElevenLabs session started with ID:", sessionId, "and options:", sessionOptions);
      return sessionId;
    } catch (error) {
      console.error("Error starting ElevenLabs conversation or getting mic permission:", error);
      const errorMessage = error.message || 'Failed to start session or get mic permission.';
      setCurrentError(errorMessage);
      alert(`Error: ${errorMessage}`);
      return null;
    }
  }, [startSession, handleNewMessage, handleError]);

  const handleEndConversation = useCallback(async () => {
    try {
      await endSession();
      console.log("ElevenLabs session ended.");
    } catch (error) {
      console.error("Error ending ElevenLabs conversation:", error);
      setCurrentError(error.message || 'Failed to end session.');
    }
  }, [endSession]);

  return {
    startConversation: handleStartConversation,
    endConversation: handleEndConversation,
    sendUserMessage,
    sendContextualUpdate,
    sendFeedback,
    status,
    isSpeaking,
    canSendFeedback,
    messages,
    error: currentError,
  };
} 